/**
 * Le scorciatoie: una colonna stretta sul bordo destro del contenuto.
 *
 * Sono le schermate che prima erano voci di menu. Restano secondarie — non
 * sono nel menu e non lo diventano — ma **portano scritto il loro nome**.
 *
 * Per un po' sono state icone sole, con l'etichetta al passaggio del mouse e
 * un punto interrogativo che apriva la legenda. Risolveva la domanda «questa
 * cos'è?» e lasciava aperta quella prima, che è «dov'è finito il rendiconto?»:
 * a quella un'etichetta che compare solo se sai già dove puntare non risponde,
 * e con il dito su un portatile non compare affatto. Trenta pixel di colonna
 * in più costano meno di un host che non trova i suoi conti.
 *
 * La targhetta al passaggio resta, perché porta anche la riga di spiegazione.
 */

import { useEffect, useRef, useState } from "react";
import { HelpCircle } from "lucide-react";
import { cx } from "../ui/primitives.tsx";
import {
  SCORCIATOIE,
  type Scorciatoia,
  type ScorciatoiaItem,
} from "./nav-items.tsx";

export function Scorciatoie({ onApri }: { onApri: (id: Scorciatoia) => void }) {
  const [legenda, setLegenda] = useState(false);
  const colonna = useRef<HTMLDivElement | null>(null);

  // Si chiude cliccando fuori o con Esc: è un pannellino, non una finestra.
  useEffect(() => {
    if (!legenda) return;
    const fuori = (event: MouseEvent) => {
      if (!colonna.current?.contains(event.target as Node)) setLegenda(false);
    };
    const esc = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLegenda(false);
    };
    document.addEventListener("mousedown", fuori);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fuori);
      document.removeEventListener("keydown", esc);
    };
  }, [legenda]);

  return (
    <div
      ref={colonna}
      className="relative hidden w-[84px] shrink-0 flex-col items-center gap-0.5 border-l border-line py-4 sm:flex"
    >
      {SCORCIATOIE.map((voce) => (
        <Icona
          key={voce.id}
          voce={voce}
          onClick={() => {
            setLegenda(false);
            onApri(voce.id);
          }}
        />
      ))}

      <span className="my-1 h-px w-6 bg-line" />

      {/* ── Il punto interrogativo, con l'elenco completo accanto ──────── */}
      <div className="relative">
        <button
          type="button"
          aria-label="Cosa sono queste icone"
          aria-expanded={legenda}
          onClick={() => setLegenda((aperta) => !aperta)}
          className={cx(
            "grid size-10 place-items-center rounded-field",
            "transition-all duration-quick ease-soft active:scale-90",
            legenda
              ? "bg-ink text-ink-inverse"
              : "text-ink-faint hover:bg-surface-muted hover:text-ink",
          )}
        >
          <HelpCircle size={17} />
        </button>

        {legenda ? (
          <div
            role="dialog"
            aria-label="Cosa sono queste icone"
            className={cx(
              "animate-pop absolute right-full top-0 z-30 mr-2.5 w-[300px] origin-top-right",
              "rounded-card-lg bg-surface p-2 shadow-lift ring-1 ring-line",
            )}
          >
            <p className="px-2.5 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
              Scorciatoie
            </p>

            {SCORCIATOIE.map((voce) => (
              <button
                key={voce.id}
                type="button"
                onClick={() => {
                  setLegenda(false);
                  onApri(voce.id);
                }}
                className="flex w-full items-center gap-3 rounded-card px-2.5 py-2 text-left transition-colors duration-quick hover:bg-surface-muted"
              >
                <span className="grid size-8 shrink-0 place-items-center rounded-field bg-surface-muted text-ink-soft">
                  <voce.Icon size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-bold leading-tight text-ink">
                    {voce.label}
                  </span>
                  <span className="block truncate text-[12px] text-ink-soft">
                    {voce.descrizione}
                  </span>
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Un'icona e la sua targhetta.
 *
 * La targhetta è pura CSS su `group-hover`: nessuno stato React, quindi compare
 * nell'istante in cui il mouse arriva invece che al giro di render dopo.
 */
function Icona({
  voce,
  onClick,
}: {
  voce: ScorciatoiaItem;
  onClick: () => void;
}) {
  return (
    <div className="group relative w-full px-1">
      <button
        type="button"
        onClick={onClick}
        className={cx(
          "flex w-full flex-col items-center gap-1 rounded-field px-1 py-2 text-ink-soft",
          "transition-all duration-quick ease-soft",
          "hover:bg-surface-muted hover:text-ink active:scale-95",
          "group-focus-within:bg-surface-muted group-focus-within:text-ink",
        )}
      >
        <voce.Icon size={18} />
        <span className="text-[11px] font-medium leading-none tracking-tight">
          {voce.label}
        </span>
      </button>

      <div
        aria-hidden
        className={cx(
          "pointer-events-none absolute right-full top-1/2 z-30 mr-2.5 w-[230px] -translate-y-1/2",
          "rounded-card bg-surface px-3 py-2 shadow-lift ring-1 ring-line",
          // Entra scivolando di poco verso l'icona: dice da dove viene.
          "origin-right scale-95 opacity-0 transition-all duration-quick ease-out",
          "group-hover:translate-x-0 group-hover:scale-100 group-hover:opacity-100",
          "group-focus-within:scale-100 group-focus-within:opacity-100",
          "translate-x-1.5",
        )}
      >
        <p className="text-[13px] font-bold leading-tight text-ink">
          {voce.label}
        </p>
        <p className="mt-0.5 text-[12px] leading-snug text-ink-soft">
          {voce.descrizione}
        </p>
      </div>
    </div>
  );
}
